import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { skapaKund, skapaInkopare, hämtaLiknandeKunder, LiknandeKund } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { TEMA } from '../theme';

interface NyInkopare {
  namn: string;
  roll: string;
  telefon: string;
  email: string;
  föredrar_typ: string;
  prisnivå: string;
  smakprofil_text: string;
}

const tomInkopare = (): NyInkopare => ({
  namn: '', roll: '', telefon: '', email: '',
  föredrar_typ: '', prisnivå: 'medel', smakprofil_text: '',
});

const kortStyle: React.CSSProperties = {
  background: TEMA.bgPanel,
  border: `1px solid ${TEMA.border}`,
  borderRadius: '8px',
  padding: '22px 24px',
  marginBottom: '16px',
};

const labelStyle: React.CSSProperties = {
  fontFamily: "'IBM Plex Mono', monospace",
  fontSize: '11px',
  color: TEMA.inkFaint,
  display: 'block',
  marginBottom: '6px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  fontSize: '14px',
  fontFamily: "'Inter', sans-serif",
  borderRadius: '2px',
  border: `1px solid ${TEMA.border}`,
  background: TEMA.bg,
  color: TEMA.ink,
  boxSizing: 'border-box',
  outline: 'none',
};

const underKortStyle: React.CSSProperties = {
  background: TEMA.bg,
  border: `1px solid ${TEMA.border}`,
  borderRadius: '6px',
  padding: '14px',
  marginBottom: '10px',
};

const btnGhostStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: '13px',
  padding: '9px 18px',
  borderRadius: '2px',
  border: `1px solid ${TEMA.border}`,
  background: 'transparent',
  color: TEMA.inkSoft,
  cursor: 'pointer',
};

export default function RegistreraKund() {
  const navigate = useNavigate();
  const { profil } = useAuth();
  const [sparar, setSparar] = useState(false);
  const [fel, setFel] = useState('');

  const [kund, setKund] = useState({
    namn: '',
    typ: 'restaurang',
    segment: 'B',
    stad: '',
    matkoncept: '',
  });

  const [inkopare, setInkopare] = useState<NyInkopare[]>([tomInkopare()]);

  // Kunder som redan finns och liknar namnet som skrivs. Frågan ställs en
  // stund efter senaste tangenttryckningen, och ett svar på ett äldre namn
  // kastas, så att listan alltid hör till det som står i fältet.
  const [liknande, setLiknande] = useState<LiknandeKund[]>([]);
  useEffect(() => {
    const namn = kund.namn.trim();
    if (namn.length < 3) {
      setLiknande([]);
      return;
    }
    let aktuell = true;
    const väntan = setTimeout(() => {
      hämtaLiknandeKunder(namn)
        .then(r => { if (aktuell) setLiknande(r.data); })
        .catch(() => { if (aktuell) setLiknande([]); });
    }, 400);
    return () => { aktuell = false; clearTimeout(väntan); };
  }, [kund.namn]);

  function uppdateraInkopare(index: number, fält: keyof NyInkopare, värde: string) {
    setInkopare(prev => prev.map((i, idx) => idx === index ? { ...i, [fält]: värde } : i));
  }

  function läggTillInkopare() {
    setInkopare(prev => [...prev, tomInkopare()]);
  }

  function taBortInkopare(index: number) {
    setInkopare(prev => prev.filter((_, idx) => idx !== index));
  }

  async function spara() {
    if (!kund.namn.trim()) {
      setFel('Ange ett namn på kunden.');
      return;
    }
    setSparar(true);
    setFel('');
    try {
      const res = await skapaKund({
        namn: kund.namn,
        typ: kund.typ,
        segment: kund.segment,
        stad: kund.stad,
        matkoncept: kund.matkoncept,
        ansvarig_säljare: profil?.namn || '',
        aktiv: true,
      });
      const kund_id = res.data.kund_id;

      for (const i of inkopare) {
        if (i.namn.trim()) {
          await skapaInkopare({
            kund_id,
            namn: i.namn,
            roll: i.roll,
            telefon: i.telefon,
            email: i.email,
            föredrar_typ: i.föredrar_typ,
            prisnivå: i.prisnivå,
            smakprofil_text: i.smakprofil_text,
          });
        }
      }

      navigate(`/kund/${kund_id}`);
    } catch (e) {
      setFel('Något gick fel – försök igen.');
    } finally {
      setSparar(false);
    }
  }

  return (
    <div>
      <button onClick={() => navigate('/alla-kunder')} style={{
        fontFamily: "'Inter', sans-serif", fontSize: '13px', color: TEMA.inkSoft, background: 'none', border: 'none',
        cursor: 'pointer', padding: 0, marginBottom: '16px',
      }}>
        ← Tillbaka
      </button>

      <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: '26px', color: TEMA.ink, margin: '0 0 24px' }}>Lägg till ny kund</h1>

      {fel && <p style={{ color: '#D9695F', fontSize: '13px', marginBottom: '16px', fontFamily: "'Inter', sans-serif" }}>{fel}</p>}

      {/* Kunduppgifter */}
      <div style={kortStyle}>
        <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: '17px', color: TEMA.ink, margin: '0 0 18px' }}>Kunduppgifter</h2>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
          <div>
            <label style={labelStyle}>Namn *</label>
            <input
              style={inputStyle}
              value={kund.namn}
              onChange={e => setKund(k => ({ ...k, namn: e.target.value }))}
              placeholder="Restaurangens namn"
            />
          </div>
          <div>
            <label style={labelStyle}>Typ</label>
            <select style={inputStyle} value={kund.typ} onChange={e => setKund(k => ({ ...k, typ: e.target.value }))}>
              <option value="restaurang">Restaurang</option>
              <option value="hotell">Hotell</option>
              <option value="bar">Bar</option>
              <option value="catering">Catering</option>
            </select>
          </div>
        </div>

        {liknande.length > 0 && (
          <div style={{ border: `1px solid ${TEMA.accent}`, borderRadius: '6px', padding: '14px 16px', marginBottom: '14px', background: TEMA.bg }}>
            <p style={{ margin: 0, fontFamily: "'Inter', sans-serif", fontSize: '13.5px', fontWeight: 500, color: TEMA.ink }}>
              Finns kunden redan?
            </p>
            <p style={{ margin: '4px 0 10px', fontFamily: "'Inter', sans-serif", fontSize: '12.5px', color: TEMA.inkSoft, lineHeight: 1.5 }}>
              {liknande.length === 1 ? 'Det här kortet har ett liknande namn.' : 'De här korten har liknande namn.'} Öppna
              kortet om det är samma ställe. Är det ett annat ställe kan du spara som vanligt.
            </p>
            {liknande.map(l => (
              <div key={l.kund_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '8px 0', borderTop: `1px solid ${TEMA.border}` }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: "'Fraunces', serif", fontSize: '15px', color: TEMA.ink }}>{l.namn}</div>
                  <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '12.5px', color: TEMA.inkSoft, marginTop: '2px' }}>
                    {[l.stad, l.fortnox_kund_nr ? 'Kund' : 'Prospect', l.ansvarig_säljare || 'Ej tilldelad'].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <button type="button" onClick={() => navigate(`/kund/${l.kund_id}`)} style={{ ...btnGhostStyle, padding: '7px 14px', whiteSpace: 'nowrap' }}>
                  Öppna
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
          <div>
            <label style={labelStyle}>Stad</label>
            <input
              style={inputStyle}
              value={kund.stad}
              onChange={e => setKund(k => ({ ...k, stad: e.target.value }))}
              placeholder="Stockholm"
            />
          </div>
          <div>
            <label style={labelStyle}>Segment</label>
            <select style={inputStyle} value={kund.segment} onChange={e => setKund(k => ({ ...k, segment: e.target.value }))}>
              <option value="A">A – Premiumkund</option>
              <option value="B">B – Standardkund</option>
              <option value="C">C – Lågprioriterad</option>
            </select>
          </div>
        </div>

        <div>
          <label style={labelStyle}>Matkoncept</label>
          <input
            style={inputStyle}
            value={kund.matkoncept}
            onChange={e => setKund(k => ({ ...k, matkoncept: e.target.value }))}
            placeholder="T.ex. nordisk finedining, bistro, pizzeria..."
          />
        </div>
      </div>

      {/* Inköpare */}
      <div style={kortStyle}>
        <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: '17px', color: TEMA.ink, margin: '0 0 18px' }}>Inköpare</h2>

        {inkopare.map((i, index) => (
          <div key={index} style={underKortStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontFamily: "'Fraunces', serif", fontSize: '14px', color: TEMA.ink }}>Inköpare {index + 1}</span>
              {inkopare.length > 1 && (
                <button onClick={() => taBortInkopare(index)} style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: TEMA.inkFaint, background: 'none', border: 'none', cursor: 'pointer' }}>
                  Ta bort
                </button>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
              <div>
                <label style={labelStyle}>Namn</label>
                <input style={inputStyle} value={i.namn} onChange={e => uppdateraInkopare(index, 'namn', e.target.value)} placeholder="För- och efternamn" />
              </div>
              <div>
                <label style={labelStyle}>Roll</label>
                <input style={inputStyle} value={i.roll} onChange={e => uppdateraInkopare(index, 'roll', e.target.value)} placeholder="Sommelier, F&B-chef..." />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
              <div>
                <label style={labelStyle}>Telefon</label>
                <input style={inputStyle} value={i.telefon} onChange={e => uppdateraInkopare(index, 'telefon', e.target.value)} placeholder="070-000 00 00" />
              </div>
              <div>
                <label style={labelStyle}>Email</label>
                <input type="email" style={inputStyle} value={i.email} onChange={e => uppdateraInkopare(index, 'email', e.target.value)} placeholder="namn@restaurang.se" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
              <div>
                <label style={labelStyle}>Föredrar typ</label>
                <select style={inputStyle} value={i.föredrar_typ} onChange={e => uppdateraInkopare(index, 'föredrar_typ', e.target.value)}>
                  <option value="">Vet ej</option>
                  <option value="rött">Rött</option>
                  <option value="vitt">Vitt</option>
                  <option value="rosé">Rosé</option>
                  <option value="mousserande">Mousserande</option>
                  <option value="naturvin">Naturvin</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Prisnivå</label>
                <select style={inputStyle} value={i.prisnivå} onChange={e => uppdateraInkopare(index, 'prisnivå', e.target.value)}>
                  <option value="låg">Låg</option>
                  <option value="medel">Medel</option>
                  <option value="hög">Hög</option>
                </select>
              </div>
            </div>

            <div>
              <label style={labelStyle}>Smakprofil (fritext)</label>
              <input style={inputStyle} value={i.smakprofil_text} onChange={e => uppdateraInkopare(index, 'smakprofil_text', e.target.value)} placeholder="T.ex. gillar mineraliska vita, undviker tanniner..." />
            </div>
          </div>
        ))}

        <button onClick={läggTillInkopare} style={btnGhostStyle}>
          + Lägg till inköpare
        </button>
      </div>

      <button
        onClick={spara}
        disabled={sparar}
        style={{
          width: '100%',
          padding: '13px',
          borderRadius: '2px',
          background: TEMA.accent,
          color: TEMA.onAccent,
          fontFamily: "'Inter', sans-serif",
          fontSize: '15px',
          fontWeight: 500,
          border: `1px solid ${TEMA.accent}`,
          cursor: sparar ? 'default' : 'pointer',
          opacity: sparar ? 0.7 : 1,
          transition: 'background 0.15s ease',
        }}
        onMouseEnter={e => { if (!sparar) (e.currentTarget as HTMLElement).style.background = TEMA.accentHover; }}
        onMouseLeave={e => { if (!sparar) (e.currentTarget as HTMLElement).style.background = TEMA.accent; }}
      >
        {sparar ? 'Sparar...' : 'Spara kund'}
      </button>
    </div>
  );
}